import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { toast } from "sonner";

import MediaAPI from "../../api/MediaAPI";
import ProjectAPI from "../../api/ProjectAPI";

import type { UseMutateFunction } from "@tanstack/react-query";

import type {
  CreateEmbedProfileResponse,
  EmbedProfile,
  EmbedProfilePayload,
  UpdateEmbedProfileParams,
} from "../../api/ProjectAPI.types";

export interface UseEmbedProfileMutationsResult {
  createEmbedProfile: (
    data: EmbedProfilePayload,
  ) => Promise<CreateEmbedProfileResponse>;
  updateEmbedProfile: (
    params: UpdateEmbedProfileParams,
  ) => Promise<EmbedProfile>;
  deleteEmbedProfile: UseMutateFunction<void, unknown, string>;
  isSaving: boolean;
  isDeleting: boolean;
}

export function useEmbedProfileMutations(): UseEmbedProfileMutationsResult {
  const queryClient = useQueryClient();

  const listQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getEmbedProfileList,
    params: [],
  });
  const optionsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getEmbedProfileOptions,
    params: [],
  });

  const invalidateProfiles = (): void => {
    queryClient.invalidateQueries(listQuery.queryKey);
    queryClient.invalidateQueries(optionsQuery.queryKey);
  };

  // Create/update leave error-toasting to the form (it maps field errors).
  const onSaveSuccess = (): void => {
    toast.success(t("editor-notif:saveSuccess"));
    invalidateProfiles();
  };

  const createMutation = useMutation({
    mutationFn: async (data: EmbedProfilePayload) =>
      ProjectAPI.createEmbedProfile(data),
    onSuccess: onSaveSuccess,
  });

  const updateMutation = useMutation({
    mutationFn: async (params: UpdateEmbedProfileParams) =>
      ProjectAPI.updateEmbedProfile(params),
    onSuccess: onSaveSuccess,
  });

  // Deleting a profile resets embed_profile_uuid on its medias.
  const deleteMutation = useMutation({
    mutationFn: async (uuid: string) => ProjectAPI.deleteEmbedProfile(uuid),
    onSuccess: () => {
      toast.success(t("editor-notif:deleteSuccess"));
      invalidateProfiles();
      queryClient.invalidateQueries([MediaAPI.getMedia.name]);
    },
    onError: () => {
      toast.error(t("editor-notif:deleteError"));
    },
  });

  return {
    createEmbedProfile: createMutation.mutateAsync,
    updateEmbedProfile: updateMutation.mutateAsync,
    deleteEmbedProfile: deleteMutation.mutate,
    isSaving: createMutation.isLoading || updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
}
